import React, { useState, useEffect } from 'react';
import { 
  Users, Eye, Globe, TrendingUp, Search, ThumbsUp, 
  MessageSquare, Download, RefreshCw, Calendar, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#292524', '#D4AF37', '#78716c', '#a8a29e', '#d6d3d1'];

const StatCard = ({ icon: Icon, label, value, subValue, color = "bg-stone-900" }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-stone-900">{value}</p>
        <p className="text-sm text-stone-500">{label}</p>
        {subValue && <p className="text-xs text-stone-400 mt-0.5">{subValue}</p>}
      </div>
    </div>
  </div>
);

const AnalyticsDashboard = ({ adminToken }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [locations, setLocations] = useState(null);
  const [behavior, setBehavior] = useState(null);
  const [trafficSources, setTrafficSources] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const [overviewRes, locationsRes, behaviorRes, trafficRes, feedbackRes] = await Promise.all([
        axios.get(`${API_URL}/admin/analytics/overview?admin_token=${adminToken}&days=${timeRange}`),
        axios.get(`${API_URL}/admin/analytics/locations?admin_token=${adminToken}`),
        axios.get(`${API_URL}/admin/analytics/behavior?admin_token=${adminToken}&days=${timeRange}`),
        axios.get(`${API_URL}/admin/analytics/traffic-sources?admin_token=${adminToken}&days=${timeRange}`),
        axios.get(`${API_URL}/admin/feedback/summary?admin_token=${adminToken}&days=${timeRange}`)
      ]);
      
      setOverview(overviewRes.data);
      setLocations(locationsRes.data);
      setBehavior(behaviorRes.data);
      setTrafficSources(trafficRes.data);
      setFeedback(feedbackRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchAnalytics();
    }
  }, [adminToken, timeRange]);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/analytics/export?admin_token=${adminToken}&days=${timeRange}`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wvc-analytics-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Analytics exported successfully');
    } catch (error) {
      toast.error('Failed to export analytics');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-stone-400" />
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard 
          icon={Users} 
          label="Total Visitors" 
          value={overview?.total_sessions || 0}
          color="bg-blue-500"
        />
        <StatCard 
          icon={Users} 
          label="New Visitors" 
          value={overview?.new_visitors || 0}
          subValue={`${overview?.returning_visitors || 0} returning`}
          color="bg-green-500"
        />
        <StatCard 
          icon={Activity} 
          label="Active Now" 
          value={overview?.active_now || 0}
          color="bg-amber-500"
        />
        <StatCard 
          icon={Eye} 
          label="Page Views" 
          value={overview?.total_page_views || 0}
          color="bg-purple-500"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Avg Pages/Session" 
          value={overview?.avg_pages_per_session || 0}
          color="bg-stone-700"
        />
      </div>

      {/* Visitor Trends Chart */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-medium text-stone-900 mb-4">Visitor Trends</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={overview?.daily_stats || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#a8a29e" />
              <YAxis tick={{ fontSize: 12 }} stroke="#a8a29e" />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="total_sessions" name="Sessions" stroke="#292524" fill="#29252420" />
              <Area type="monotone" dataKey="page_views" name="Page Views" stroke="#D4AF37" fill="#D4AF3720" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Location Intelligence */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-stone-400" />
            Top Locations
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {locations?.top_cities?.length > 0 ? (
              locations.top_cities.map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center text-xs font-medium">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-stone-900">{loc.city || 'Unknown'}</p>
                      <p className="text-xs text-stone-500">{loc.country}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-stone-700">{loc.visit_count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-stone-500 text-center py-4">No location data yet</p>
            )}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4">Traffic Sources</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={trafficSources?.sources || []}
                  dataKey="count"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={({ source, percentage }) => `${source} (${percentage}%)`}
                >
                  {trafficSources?.sources?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Behavior Intelligence */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-stone-400" />
            Most Viewed Pages
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {behavior?.top_pages?.map((page, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <span className="text-sm text-stone-600 truncate max-w-[200px]">{page.page}</span>
                <span className="text-sm font-medium text-stone-900">{page.view_count} views</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Searches */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-stone-400" />
            Popular Venue Searches
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {behavior?.top_searches?.map((search, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <span className="text-sm text-stone-600 truncate max-w-[200px]">{search.name}</span>
                <span className="text-sm font-medium text-stone-900">{search.search_count} searches</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Helpful/Not-Helpful Stats */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-green-500" />
            User Feedback Overview
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{feedback?.total_helpful_votes || 0}</p>
              <p className="text-xs text-green-700">Helpful</p>
            </div>
            <div className="text-center p-3 bg-stone-50 rounded-lg">
              <p className="text-2xl font-bold text-stone-600">{feedback?.total_not_helpful_votes || 0}</p>
              <p className="text-xs text-stone-500">Not Helpful</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{feedback?.helpful_ratio || 0}%</p>
              <p className="text-xs text-blue-700">Helpful Ratio</p>
            </div>
          </div>
          
          {/* Venue feedback breakdown */}
          <p className="text-xs text-stone-500 mb-2 font-medium">Feedback by Venue</p>
          <div className="space-y-2 max-h-36 overflow-y-auto">
            {feedback?.helpful_by_venue?.slice(0, 6).map((venue, idx) => (
              <div key={idx} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
                <span className="text-sm text-stone-600 truncate max-w-[140px]">{venue.venue_name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600">{venue.helpful_count || 0} 👍</span>
                  <span className="text-xs text-stone-400">{venue.not_helpful_count || 0} 👎</span>
                  {venue.has_negative_spike && (
                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">!</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Negative spike alerts */}
          {feedback?.negative_spike_venues?.length > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 font-medium">
                ⚠️ {feedback.negative_spike_venues.length} venue(s) with negative feedback spikes
              </p>
            </div>
          )}
        </div>

        {/* Feature Requests */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Feature Suggestions ({feedback?.feature_requests?.length || 0})
          </h3>
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {feedback?.feature_requests?.length > 0 ? (
              feedback.feature_requests.slice(0, 10).map((req, idx) => (
                <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-700">{req.feature_text}</p>
                  <p className="text-xs text-stone-400 mt-1">{new Date(req.created_at).toLocaleDateString()}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-stone-500 text-center py-4">No feature suggestions yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Helpful Trend */}
      {feedback?.daily_helpful_trend?.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-medium text-stone-900 mb-4">Feedback Trend Over Time</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={feedback.daily_helpful_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#a8a29e" />
                <YAxis tick={{ fontSize: 12 }} stroke="#a8a29e" />
                <Tooltip />
                <Bar dataKey="count" name="Helpful Votes" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
